(() => {
  if (customElements.get('sw-product-photo-cycle')) return;
  class ProductPhotoCycle extends HTMLElement {
    connectedCallback() {
      if (this.host) return;
      this.host = this.parentElement;
      this.focusHost = this.host.closest('a') || this.host;
      this.photos = Array.from(this.querySelector('template')?.content.querySelectorAll('img') || []);
      if (this.photos.length < 2) return;
      this.desktop = matchMedia('(any-hover: hover) and (any-pointer: fine)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.host.classList.add('sw-photo-cycle-host');
      this.enter = (event) => {
        if (event.type === 'pointerenter' && event.pointerType !== 'mouse') return;
        this.start();
      };
      this.leave = (event) => {
        if (event.type === 'focusout' && this.focusHost.contains(event.relatedTarget)) return;
        this.stop();
      };
      this.policy = () => {
        if (!this.desktop.matches || this.reduced.matches) {
          this.stop();
          this.host.classList.remove('sw-photo-cycle-ready');
        } else this.host.classList.add('sw-photo-cycle-ready');
      };
      this.visibility = () => { if (document.hidden) this.stop(); };
      this.host.addEventListener('pointerenter', this.enter);
      this.host.addEventListener('pointerleave', this.leave);
      this.focusHost.addEventListener('focusin', this.enter);
      this.focusHost.addEventListener('focusout', this.leave);
      this.desktop.addEventListener('change', this.policy);
      this.reduced.addEventListener('change', this.policy);
      document.addEventListener('visibilitychange', this.visibility);
      this.policy();
    }
    start() {
      if (this.active || !this.desktop.matches || this.reduced.matches || document.hidden) return;
      this.active = true;
      this.generation = (this.generation || 0) + 1;
      this.index = 0;
      // Preserve the actual image crop and current swatch image on exit.
      const primary = Array.from(this.host.children).find(node => node.tagName === 'IMG');
      if (primary) {
        const style = getComputedStyle(primary);
        this.style.setProperty('--sw-photo-cycle-fit', style.objectFit);
        this.style.setProperty('--sw-photo-cycle-position', style.objectPosition);
      }
      this.schedule(this.generation, 350);
    }
    schedule(generation, delay = 1800) {
      this.timer = setTimeout(() => this.advance(generation), delay);
    }
    async advance(generation) {
      if (!this.active || generation !== this.generation || !this.isConnected) return;
      this.index = (this.index + 1) % this.photos.length;
      const next = this.photos[this.index].cloneNode(true);
      // Templates do not download images; only the hovered product loads its next photo.
      next.sizes = `${Math.ceil(this.host.getBoundingClientRect().width)}px`;
      this.append(next);
      try { await next.decode(); } catch {
        next.remove();
        if (this.active && generation === this.generation) this.schedule(generation);
        return;
      }
      if (!this.active || generation !== this.generation || !this.isConnected) { next.remove(); return; }
      const previous = this.current;
      // Flush initial opacity before the crossfade, without delaying navigation.
      void next.offsetWidth;
      next.classList.add('is-visible');
      previous?.classList.remove('is-visible');
      this.current = next;
      if (previous) setTimeout(() => previous.remove(), 350);
      this.schedule(generation);
    }
    stop() {
      this.active = false;
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.timer);
      this.current = null;
      this.querySelectorAll(':scope > img').forEach(image => image.remove());
    }
    disconnectedCallback() {
      this.stop();
      if (!this.host) return;
      this.host.removeEventListener('pointerenter', this.enter);
      this.host.removeEventListener('pointerleave', this.leave);
      this.focusHost.removeEventListener('focusin', this.enter);
      this.focusHost.removeEventListener('focusout', this.leave);
      this.desktop?.removeEventListener('change', this.policy);
      this.reduced?.removeEventListener('change', this.policy);
      document.removeEventListener('visibilitychange', this.visibility);
      this.host.classList.remove('sw-photo-cycle-host', 'sw-photo-cycle-ready');
      this.host = null;
    }
  }
  customElements.define('sw-product-photo-cycle', ProductPhotoCycle);
})();
